import { Link } from "react-router-dom";
import UpdatesLogTab from "./UpdatesLogTab";
import SystemLogTab from "./SystemLogTab";

/**
 * 系統資訊頁——說明／日誌 TAB 族（模塊化跟進 2026-09-20：自 SystemInfoPage.tsx 拆出，
 * 原文逐字搬遷）。純展示組件：sys／rich／llm／syslog／log／cheatsheet／overview／
 * prompt／chat／learning／onboard；品牌／對話上限兩個交互 TAB 喺 SystemInfoSettings。
 */

export type DiagramTab =
  | "sys" | "llm" | "cheatsheet" | "brand" | "abuse" | "corekb" | "rich" | "trial" | "trialwalk"
  | "overview" | "chat" | "learning" | "onboard" | "prompt" | "employees" | "log" | "syslog";

export function DiagramContent({
  tab,
  llmKeys,
  llmKeysLoading,
}: {
  tab: DiagramTab;
  llmKeys: { deepseek?: { configured: boolean; masked: string | null }; zhipu?: { configured: boolean; masked: string | null } } | null;
  llmKeysLoading: boolean;
}) {
  return (
    <>
      {/* ═══════════ TAB：🏠 概覽（端點／部署版本／API 參考）═══════════ */}
      {tab === "sys" && (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* API 端點（2026-09-12 安全收窄：內部 workers.dev 直達已關——只經 wtsaibot.com gateway；內部主機名唔再顯示） */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-emerald-400 mb-4">
            🔌 端點
          </h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-400">AI Core（唯一入口）</span>
              <span className="text-slate-300 font-mono text-xs">wtsaibot.com/api/core/*（內部直達已隱藏）</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">WhatsApp Connector（唯一入口）</span>
              <span className="text-slate-300 font-mono text-xs">wtsaibot.com/webhook/*（內部直達已隱藏）</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">分銷域（growth Worker）</span>
              <span className="text-slate-300 font-mono text-xs">wtsaibot.com/api/referrals/*・/r/*・/ref/*</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">開通精靈</span>
              <a
                href="https://wtsaibot.com/setup/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 hover:underline"
              >
                wtsaibot.com/setup
              </a>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">商戶門戶</span>
              <a
                href="https://wtsaibot.com/merchant/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 hover:underline"
              >
                wtsaibot.com/merchant
              </a>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">產品官網</span>
              <a
                href="https://wtsaibot.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 hover:underline"
              >
                wtsaibot.com
              </a>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-400">付款頁（billing）</span>
              <a
                href="https://billing.wtsaibot.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-400 hover:underline"
              >
                billing.wtsaibot.com
              </a>
            </div>
          </div>
        </div>

        {/* 部署版本 */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-emerald-400 mb-4">
            🚀 部署版本
          </h2>
          <div className="space-y-2 text-sm text-slate-300">
            <div className="flex justify-between border-b border-slate-800 pb-2">
              <span>Gateway</span>
              <span className="font-mono text-slate-400">aibot-gateway</span>
            </div>
            <div className="flex justify-between border-b border-slate-800 pb-2">
              <span>AI Core</span>
              <span className="font-mono text-slate-400">aibot-core</span>
            </div>
            <div className="flex justify-between border-b border-slate-800 pb-2">
              <span>WhatsApp Connector</span>
              <span className="font-mono text-slate-400">aibot-wa</span>
            </div>
            <div className="flex justify-between border-b border-slate-800 pb-2">
              <span>Growth（兩層分銷）</span>
              <span className="font-mono text-slate-400">aibot-growth</span>
            </div>
            <div className="flex justify-between border-b border-slate-800 pb-2">
              <span>事件匯流排</span>
              <span className="font-mono text-slate-400">Queue: aibot-order-events（＋DLQ）</span>
            </div>
            <div className="flex justify-between border-b border-slate-800 pb-2">
              <span>Dashboard Pages</span>
              <span className="font-mono text-slate-400">aibot-dashboard</span>
            </div>
            <div className="flex justify-between border-b border-slate-800 pb-2">
              <span>Portal Pages</span>
              <span className="font-mono text-slate-400">aibot-portal</span>
            </div>
            <div className="flex justify-between border-b border-slate-800 pb-2">
              <span>Wizard Pages</span>
              <span className="font-mono text-slate-400">aibot-wizard</span>
            </div>
            <div className="flex justify-between">
              <span>Billing Pages</span>
              <span className="font-mono text-slate-400">aibot-billing</span>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-slate-500">
            引擎、模型與系統預設 LLM API Key 已獨立到「🧠 LLM 與模型」TAB。
          </p>
        </div>

        {/* 模組化拓撲（2026-10-25 Phase 1-3 上線） */}
        <div className="md:col-span-2 bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-emerald-400 mb-2">
            🏗️ 模組化拓撲（2026-10-25 Phase 1–3 上線）
          </h2>
          <p className="text-xs text-slate-500 mb-3">
            四域分層＋獨立 Worker——「哪個域壞了、影響誰」30 秒可界定。詳見 <code className="text-slate-300">docs/ARCHITECTURE.md</code> §9 與 <code className="text-slate-300">docs/BUILD_LOG.md</code> 2026-10-25 三段。
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
            {[
              { name: "core（AI 本體）", desc: "ask／KB／prompt／quota／voice——ai-core 與 connector 各有 modules/core", tag: "text-emerald-400" },
              { name: "growth（兩層分銷）", desc: "referral v1/v2＋佣金凍結＋夥伴通知——獨立 aibot-growth Worker", tag: "text-emerald-400" },
              { name: "provision（供應鏈）", desc: "onboard／KPay／renumber／channel——ai-core 與 connector 分層（物理拆分待裁決）", tag: "text-amber-400" },
              { name: "platform（共享內核）", desc: "syslog／中央通知線（85262322466 唯一來源 ports.ts）／Ports 契約／事件匯流排", tag: "text-emerald-400" },
            ].map((d) => (
              <div key={d.name} className="bg-slate-950 rounded-lg border border-slate-800 p-3">
                <div className={`font-semibold mb-1 ${d.tag}`}>{d.name}</div>
                <div className="text-slate-400">{d.desc}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            邊界鐵律 R1–R5：R1 分銷不插 AI 主流程（intent 註冊制）｜R2 跨域只走 Port（RPC／Queue）｜R3 開通完成只發事件（tenant.provisioned 上 Queue，凍結 CAS 冪等可重放）｜R4 關鍵字註冊制路由｜R5 禁止靜默 return（guard／事件全留 system_logs）。部署序：growth → ai-core → connector → gateway（binding 依賴）。
          </div>
        </div>

        {/* 模組化 Addon 一覽 */}
        <div className="md:col-span-2 bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-emerald-400 mb-4">
            🧩 模組化 Addon 一覽
          </h2>
          <p className="text-xs text-slate-500 mb-4">
            新功能以 Addon 形式接入，獨立開關、獨立計費、獨立開發。接入規範見 <code className="text-slate-300">docs/modular-addon-framework.md</code>。
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
            {[
              { name: "CRM Lite", desc: "客戶名冊＋跟進＋自動 TAG", status: "已上線", path: "packages/ai-core/src/crm/" },
              { name: "AI 秘書", desc: "個人知識庫＋語音跟進草擬", status: "已上線", path: "packages/ai-core/src/secretary/" },
              { name: "Voice Notes", desc: "語音會議記錄＋轉寫＋場景後處理", status: "已上線", path: "packages/ai-core/src/voice-notes/" },
              { name: "報價單 PDF", desc: "三重門收集＋自動生成＋追單", status: "已上線", path: "packages/ai-core/src/quote.ts" },
              { name: "推薦計劃", desc: "經銷商模式＋佣金結算", status: "已上線", path: "packages/ai-core/src/referral.ts" },
              { name: "月度配額", desc: "Lite 2,000／Pro 6,000＋超額軟着陸", status: "已上線", path: "packages/ai-core/src/monthly-quota.ts" },
              { name: "billing 子域", desc: "手動付款頁＋FPS/PayMe/銀行", status: "已上線", path: "onboarding/billing-site/" },
              { name: "Analyst pitch", desc: "AI 員工分析員 MVP", status: "進行中", path: "pitch-analyst/" },
            ].map((m) => (
              <div key={m.name} className="bg-slate-950/40 border border-slate-800 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-200">{m.name}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${m.status === "已上線" ? "bg-emerald-600/20 text-emerald-400" : "bg-amber-600/20 text-amber-400"}`}>
                    {m.status}
                  </span>
                </div>
                <p className="text-slate-400 text-[11px]">{m.desc}</p>
                <p className="text-slate-600 text-[10px] font-mono mt-1">{m.path}</p>
              </div>
            ))}
          </div>
        </div>

        {/* API 文件 */}
        <div className="md:col-span-2 bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h2 className="text-lg font-semibold text-emerald-400 mb-4">
            📚 API 參考
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-400 uppercase border-b border-slate-700">
                <tr>
                  <th className="text-left px-4 py-2">方法</th>
                  <th className="text-left px-4 py-2">路徑</th>
                  <th className="text-left px-4 py-2">說明</th>
                  <th className="text-left px-4 py-2">認證</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                <tr className="border-b border-slate-800">
                  <td className="px-4 py-2 font-mono text-emerald-400">POST</td>
                  <td className="px-4 py-2 font-mono">/ask</td>
                  <td className="px-4 py-2">AI 對話查詢</td>
                  <td className="px-4 py-2">Bearer Token</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="px-4 py-2 font-mono text-emerald-400">POST</td>
                  <td className="px-4 py-2 font-mono">/admin/ingest</td>
                  <td className="px-4 py-2">上傳知識庫</td>
                  <td className="px-4 py-2">x-admin-key</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="px-4 py-2 font-mono text-emerald-400">GET</td>
                  <td className="px-4 py-2 font-mono">/admin/tenants</td>
                  <td className="px-4 py-2">列出租戶</td>
                  <td className="px-4 py-2">x-admin-key</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="px-4 py-2 font-mono text-emerald-400">GET</td>
                  <td className="px-4 py-2 font-mono">/admin/chat-logs</td>
                  <td className="px-4 py-2">查詢對話日誌</td>
                  <td className="px-4 py-2">x-admin-key</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="px-4 py-2 font-mono text-emerald-400">GET</td>
                  <td className="px-4 py-2 font-mono">/admin/llm-keys-status</td>
                  <td className="px-4 py-2">系統預設 LLM Key 狀態</td>
                  <td className="px-4 py-2">x-admin-key</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="px-4 py-2 font-mono text-emerald-400">POST</td>
                  <td className="px-4 py-2 font-mono">/internal/init-quota</td>
                  <td className="px-4 py-2">設定額度</td>
                  <td className="px-4 py-2">x-admin-key</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="px-4 py-2 font-mono text-emerald-400">GET</td>
                  <td className="px-4 py-2 font-mono">/admin/ops-overview</td>
                  <td className="px-4 py-2">營運透視數據（30 日對話／成本／毛利）</td>
                  <td className="px-4 py-2">x-admin-key</td>
                </tr>
                <tr className="border-b border-slate-800">
                  <td className="px-4 py-2 font-mono text-emerald-400">POST</td>
                  <td className="px-4 py-2 font-mono">/portal/ask</td>
                  <td className="px-4 py-2">商戶教Bot 驗收對話（portal 門戶用）</td>
                  <td className="px-4 py-2">x-portal-key</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 font-mono text-emerald-400">POST</td>
                  <td className="px-4 py-2 font-mono">/internal/webhook/subscription</td>
                  <td className="px-4 py-2">金流 Webhook</td>
                  <td className="px-4 py-2">X-Webhook-Secret</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}

      {/* ═══════════ TAB：🎛️ 互動訊息（Rich Media 發送層）═══════════ */}
      {tab === "rich" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 發送層位置與鐵律 */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-emerald-400 mb-2">🎛️ 互動發送層（rich-media.ts）</h2>
            <p className="text-xs text-slate-400 mb-4 leading-relaxed">
              CloudWAPI 按鈕／選單／圖片等互動訊息嘅<span className="text-amber-400">全平台唯一發送面</span>
              （2026-09-19 P1 收編，connector <code className="text-slate-300">src/rich-media.ts</code>）：
              AI 標記協議解析＋send-list／send-media／send-button 三個發送器＋平台分流與地雷一處把關。
              referral／eshop 要互動發送一律 import 呢個面，唔准自帶 fetch。
            </p>
            <div className="space-y-2 text-xs">
              <div className="bg-slate-950 rounded-lg p-3 border border-slate-800">
                <span className="text-emerald-300 font-semibold">增益通道鐵律</span>
                <p className="text-slate-400 mt-1">文字正文永遠先行獨立送達（部分手機連按鈕訊息文字都唔渲染）；互動發送任何失敗只記錄、由調用方 fallback 純文字，絕不阻主流程。</p>
              </div>
              <div className="bg-slate-950 rounded-lg p-3 border border-slate-800">
                <span className="text-emerald-300 font-semibold">AI 標記協議（三式）</span>
                <p className="text-slate-400 mt-1 font-mono text-[10.5px] leading-relaxed">
                  ━━LIST━━&#123;"title"…"sections"&#125;━━END━━（原生選單）<br/>
                  ━━BTN━━&#123;"prompt","buttons"&#125;━━END━━（封閉選擇題掣）<br/>
                  ━━MEDIA━━&#123;"url","caption"&#125;━━END━━（圖片）
                </p>
                <p className="text-slate-500 mt-1">模型輸出橫線變體（━─—–一）＋未閉合截斷都有容錯兜底，殘骸永遠唔會見客。</p>
              </div>
            </div>
          </div>
          {/* 平台地雷 */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-amber-400 mb-2">⚠️ CloudWAPI 平台地雷（一處管）</h2>
            <div className="space-y-2 text-xs">
              {[
                ["Set A／Set B 分流", "send-list 結構（.com sections vs .in 扁平 list 1-10 項）；generate-qr method 相反（A=GET／B=POST）"],
                ["send-list Set B footer 必帶", "文檔話可選，實測缺 footer 一律 400 Invalid parameters——空字串都通過"],
                ["多掣禁帶 footer＋url:null", "multi-button＋footer＋url:null 同帶 → 400；單掣＋footer＋url:null OK——品牌行入 message 正文"],
                ["button[] 上限 5／list 1-10", "超限即拒；掣面 20 字／選項 60 字截斷"],
                ["圖片只收 JPEG 直鏈", "WebP 係靜默失敗（規則 66）；私網／非 http URL 由 SSRF 閘攔截"],
                ["200＋status:false 假成功", "Set B 出錯會 HTTP 200——兩級檢查先算真成功（手冊 Q9）"],
              ].map(([t, d]) => (
                <div key={t} className="bg-slate-950 rounded-lg p-3 border border-slate-800">
                  <span className="text-amber-300 font-semibold">{t}</span>
                  <p className="text-slate-400 mt-1">{d}</p>
                </div>
              ))}
            </div>
          </div>
          {/* 功能旗現況 */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6 md:col-span-2">
            <h2 className="text-lg font-semibold text-sky-400 mb-2">🎚️ 功能旗現況（rich_media，預設關）</h2>
            <p className="text-xs text-slate-400 mb-3 leading-relaxed">
              旗開咗先有標記路徑；未開租戶完全零改動（AI 標記照舊當文字處理）。轉真人邀請／通知按鈕屬系統流程，唔受呢支旗管。
              2026-09-19 收編時實況：<span className="text-emerald-300 font-mono">admin、ecorp</span> 已開。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div className="bg-slate-950 rounded-lg p-3 border border-slate-800">
                <span className="text-slate-300 font-semibold">開咗想試掣</span>
                <p className="text-slate-400 mt-1">同 AI 講「俾選項我揀」——封閉式問題會出 ━━BTN━━ 掣；多項服務介紹有機會出選單。</p>
              </div>
              <div className="bg-slate-950 rounded-lg p-3 border border-slate-800">
                <span className="text-slate-300 font-semibold">referral magic link</span>
                <p className="text-slate-400 mt-1">推薦計劃按鈕（2026-09-19 修正）：原本錯 envelope 令按鈕發不出，已改行共享面（url 型單掣直開）。</p>
              </div>
              <div className="bg-slate-950 rounded-lg p-3 border border-slate-800">
                <span className="text-slate-300 font-semibold">P2 補漏偵測（未實施）</span>
                <p className="text-slate-400 mt-1">AI 冇出標記但文字係選擇題時自動升級按鈕——spec 對齊後先動工。</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ TAB：🧠 LLM 與模型（引擎層級／Key 狀態／更新指引）═══════════ */}
      {tab === "llm" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 引擎層級矩陣 */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-emerald-400">
                🏎️ 引擎層級矩陣
              </h2>
              <a
                href="#/engine-rules"
                className="text-xs px-2.5 py-1 bg-emerald-700/20 hover:bg-emerald-700/30 text-emerald-300 rounded border border-emerald-700/30 transition-colors"
              >
                ⚡ 詳細引擎規則 →
              </a>
            </div>
            <div className="space-y-3 text-sm">
              <div className="bg-emerald-950/30 border border-emerald-700/40 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-emerald-300">Premium 層</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-600/20 text-emerald-300 border border-emerald-600/30">
                    Pro／Legacy／Lite 頭兩個月／Trial
                  </span>
                </div>
                <ul className="text-xs text-slate-400 space-y-1 list-disc list-inside">
                  <li>文字：DeepSeek <code className="text-slate-300">deepseek-v4-flash</code></li>
                  <li>識圖：DeepSeek <code className="text-slate-300">deepseek-v4-flash-vision-exp</code></li>
                  <li>空內容自動重試一次；無 GLM 後備</li>
                </ul>
              </div>
              <div className="bg-slate-950/40 border border-slate-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-slate-300">Basic 層</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-400 border border-slate-600/40">
                    Lite 兩個月期滿後
                  </span>
                </div>
                <ul className="text-xs text-slate-400 space-y-1 list-disc list-inside">
                  <li>文字：Zhipu <code className="text-slate-300">glm-4-flash</code></li>
                  <li>識圖：Zhipu <code className="text-slate-300">glm-4v-flash</code></li>
                  <li>後備鏈：GLM 空回覆 → DeepSeek → CF Workers AI（eco 模式）</li>
                </ul>
              </div>
              <p className="text-[11px] text-slate-500">
                方案層級（plan_tier + premium_until）會覆蓋租戶原本嘅 llm_provider / llm_model。租戶唔再需要自行選擇模型。
              </p>
            </div>
          </div>

          {/* 系統預設 LLM API Key */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-emerald-400 mb-4">
              🔑 系統預設 LLM API Key
            </h2>
            {llmKeysLoading ? (
              <div className="text-sm text-slate-400">載入中...</div>
            ) : !llmKeys ? (
              <div className="text-sm text-rose-400">無法載入狀態</div>
            ) : (
              <div className="space-y-3">
                <div className="flex justify-between items-center border-b border-slate-800 pb-2 text-sm">
                  <span className="text-slate-300">DeepSeek</span>
                  <span className={llmKeys.deepseek?.configured ? "text-emerald-400 font-mono" : "text-slate-500"}>
                    {llmKeys.deepseek?.configured ? `已設置（${llmKeys.deepseek.masked}）` : "未設置"}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-slate-300">Zhipu（GLM）</span>
                  <span className={llmKeys.zhipu?.configured ? "text-emerald-400 font-mono" : "text-slate-500"}>
                    {llmKeys.zhipu?.configured ? `已設置（${llmKeys.zhipu.masked}）` : "未設置"}
                  </span>
                </div>
                <div className="mt-4 p-3 bg-amber-950/20 border border-amber-700/30 rounded-lg text-xs text-amber-200/80">
                  <strong>安全備註：</strong>API Key 係 Worker secrets，唔會喺瀏覽器端輸入或儲存。下方只顯示「有冇設」同尾碼，方便排查「冇 key 導致 AI 回覆空」等問題。
                </div>
              </div>
            )}
          </div>

          {/* 更新 Key 指引 */}
          <div className="md:col-span-2 bg-slate-900 rounded-xl border border-slate-800 p-6">
            <h2 className="text-lg font-semibold text-emerald-400 mb-4">
              📝 更新系統預設 Key（wrangler）
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="bg-slate-950/40 border border-slate-700/50 rounded-lg p-4">
                <p className="text-slate-300 font-semibold mb-2">DeepSeek</p>
                <code className="block text-xs font-mono text-sky-300 bg-slate-950 p-3 rounded border border-slate-800">
                  cd packages/ai-core<br />
                  echo -n &quot;your-key&quot; | npx wrangler secret put DEEPSEEK_API_KEY
                </code>
              </div>
              <div className="bg-slate-950/40 border border-slate-700/50 rounded-lg p-4">
                <p className="text-slate-300 font-semibold mb-2">Zhipu（GLM）</p>
                <code className="block text-xs font-mono text-sky-300 bg-slate-950 p-3 rounded border border-slate-800">
                  cd packages/ai-core<br />
                  echo -n &quot;your-key&quot; | npx wrangler secret put ZHIPUAI_API_KEY
                </code>
              </div>
            </div>
            <p className="mt-4 text-[11px] text-slate-500">
              更新後無需改程式碼，ai-core worker 下次收到請求即使用新 key。建議更新後喺門戶試玩場發一則測試訊息確認 AI 回覆正常。
            </p>
          </div>
        </div>
      )}

      {/* ═══════════ TAB：🚨 系統事件（自動）═══════════ */}
          {tab === "syslog" && <SystemLogTab />}

      {/* ═══════════ TAB：📋 更新日誌 ═══════════ */}
          {tab === "log" && <UpdatesLogTab />}

          {/* ═══════════ TAB：⚡ 速查（cheat sheet）═══════════ */}
          {tab === "cheatsheet" && (
          <div className="space-y-6">

            {/* ── 一、一則訊息的一生 ── */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
              <h3 className="text-base font-bold text-emerald-400 mb-3">📨 一則訊息的一生（由入到出）</h3>
              <div className="flex flex-wrap items-center gap-1.5 text-xs font-mono">
                {[
                  ["客人 WhatsApp", "emerald"],
                  ["WTS Webhook 平台（.in/.com）", "slate"],
                  ["webhook → aibot-wa", "sky"],
                  ["Session DO（合併 1.2s・打字節奏）", "sky"],
                  ["ai-core ask()", "sky"],
                  ["RAG 檢索知識庫", "violet"],
                  ["LLM 生成（Premium=DeepSeek（識圖-vision-exp）／Basic=glm 免費層；Vision 睇圖）", "violet"],
                  ["send-message（文字/圖片/選單）", "sky"],
                  ["客人收到的 1-3 則回覆", "emerald"],
                ].map(([label], i, arr) => (
                  <span key={i} className="flex items-center gap-1.5">
                    <span className="px-2.5 py-1.5 bg-slate-800 border border-slate-600 rounded text-slate-200">{label}</span>
                    {i < arr.length - 1 && <span className="text-emerald-500">→</span>}
                  </span>
                ))}
              </div>
              <div className="mt-3 text-xs text-slate-400 space-y-1">
                <p>↳ 唔識答／客人嬲／打「轉真人」→ <span className="text-amber-300">handoff 分支</span>：AI 暫停 30 分鐘＋WhatsApp 通知老闆手機（附 /c 短連結控制台）＋接管完結自動學習入知識庫</p>
                <p>↳ 租戶知識庫答唔上（低信心）→ <span className="text-violet-300">Core KB 平台答法層借力</span>（全租戶對話蒸餾嘅通用答法；/system 審批上架；只借用答法、事實仍以租戶 KB 為準）</p>
                <p>↳ 試用額度用盡 → 禮貌話俾客人知＋通知老闆（唔會已讀不回）</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* ── 二、入口 URL ── */}
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
                <h3 className="text-base font-bold text-emerald-400 mb-3">🌐 入口 URL 邊個去邊度</h3>
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-slate-800">
                    {[
                      ["wtsaibot.com/ ⭐", "產品官網＝landing（客人第一觸點）"],
                      ["wtsaibot.com/system", "系統資訊（設定／說明／日誌＋🌀 Core KB 蒸餾審批）"],
                      ["wtsaibot.com/admin", "管理後台（你而家呢度）"],
                      ["aibot.jkdcoding.com/admin/", "後台對等入口（gateway 三域之一）；⚠️ aibot-admin.jkdcoding.com Pages 直連＝白屏，永遠唔好用"],
                      ["wtsaibot.com/merchant", "商戶門戶 SaaS（知識庫/對話/接管記錄/設定/教Bot 自助）"],
                      ["wtsaibot.com/setup", "開通精靈＋/setup/bind 綁定頁（?t=&w= 專屬連結）"],
                      ["wtsaibot.com/webhook/<token>", "WTS Webhook 轉發接收端（gateway 內部直達 wa Worker）"],
                      ["wtsaibot.com/c/<短碼>", "轉真人控制台（PIN＝手機尾4位）"],
                      ["（內部已隱藏）", "AI 引擎直達已關——一律經 wtsaibot.com gateway"],
                    ].map(([url, desc]) => (
                      <tr key={url}>
                        <td className="py-2 pr-3 font-mono text-sky-300 align-top whitespace-nowrap">{url}</td>
                        <td className="py-2 text-slate-300">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── 三、租戶 WhatsApp 三件套 ── */}
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
                <h3 className="text-base font-bold text-emerald-400 mb-3">📱 租戶 WhatsApp 配置（邊樣管邊樣）</h3>
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-slate-800">
                    {[
                      ["API Key", "WTS Webhook 帳號憑證——出站發送/綁定/心跳全靠佢"],
                      ["Sender 號碼", "綁定嘅 WhatsApp 號碼（客人見到嗰個）"],
                      ["WTS Webhook Endpoint", "留空＝.com 新平台；.in 舊平台帳號要填（出站/綁定自動跟）"],
                      ["Webhook 轉發", "⚠️ 喺 WTS Webhook 後台逐帳號設一次（貼 /webhook/<token>）——漏設＝綁定成功但收唔到客"],
                    ].map(([k, v]) => (
                      <tr key={k}>
                        <td className="py-2 pr-3 font-semibold text-slate-200 align-top whitespace-nowrap">{k}</td>
                        <td className="py-2 text-slate-400">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── 四、功能開關 ── */}
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
                <h3 className="text-base font-bold text-emerald-400 mb-3">🎛️ 功能開關一覽（租戶 → 功能頁）</h3>
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-slate-800">
                    {[
                      ["人工接管＋通知", true, "唔識答即刻通知老闆手機＋AI 暫停30分鐘"],
                      ["智能分流", true, "RAG 冇信心唔硬答（誠實紅線）"],
                      ["知識庫學習迴圈", true, "真人接管完結→AI 建議入 KB→人審批"],
                      ["DeepSeek 路由", true, "Premium 層主力；Basic 層 GLM 故障時接手後備"],
                      ["GLM 免費主力路由", false, "Basic 層（Lite 期滿）免費引擎優先"],
                      ["圖片辨識 Vision", true, "客人傳圖 AI 識睇（一圖＝一額度，全方案標配）"],
                      ["豐富模態回覆", false, "AI 識發原生選單/圖片（━━LIST━━ 標記協議；.in／.com 已按平台分流 2026-09-15）"],
                      ["AI 自動回覆（靜音鎖）", true, "關閉＝該租戶 AI 完全靜音（KB 準備期用）"],
                      ["閘道離線警示", true, "offline_alerts 逐租戶開關——關閉後唔收離線通知"],
                      ["自動報價單 PDF", false, "quote_pdf（E-Corp 首例）：對話收齊資料自動出報價單"],
                      ["反濫用上限", true, "session 上限 Lite 10／Pro 15（閒置 30 分鐘重置）＋跨號碼 burst limit Lite 15min/20／Pro 15min/50；後台 🛡️ 反濫用上限頁可配；到數＝soft-stop 轉真人"],
                    ].map(([name, on, desc]) => (
                      <tr key={name as string}>
                        <td className="py-2 pr-2 whitespace-nowrap">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${on ? "bg-emerald-900/50 text-emerald-300" : "bg-slate-800 text-slate-400"}`}>
                            {on ? "預設 ON" : "預設 OFF"}
                          </span>
                        </td>
                        <td className="py-2 pr-2 font-semibold text-slate-200 whitespace-nowrap">{name}</td>
                        <td className="py-2 text-slate-400">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── 五、監控 ── */}
              <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
                <h3 className="text-base font-bold text-emerald-400 mb-3">📡 監控五件套（壞咗會 WhatsApp 通知你）</h3>
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-slate-800">
                    {[
                      ["閘道心跳", "每日兩次（HKT 朝9晚9）", "金絲雀真實發送測試；離線→租戶＋管理員手機收警示（6小時重提）；可 POST /admin/heartbeat-run 手動即刻跑"],
                      ["RAG 煙霧測試", "每小時", "向量 self-match 探測；防 Vectorize 靜默死"],
                      ["開通跟進", "每小時", "已發歡迎包未開業嘅單：T+24h 卡點定向貼士（只提未完成嗰步，四語）；T+48h 停滯→ops 手機收「⏰ 開通停滯」人手跟進"],
                      ["Webhook 日誌", "每筆即記", "入站有冇到、被唔被拒，30日保留"],
                      ["每日摘要", "朝早 10:00", "各租戶對話量/handoff 一則訊息發管理員"],
                      ["報價追單", "每 15 分鐘掃一次", "quote_pdf 租戶第 2／4 日自動追單（簽回人手標記）"],
                    ].map(([name, freq, desc]) => (
                      <tr key={name as string}>
                        <td className="py-2 pr-2 font-semibold text-slate-200 whitespace-nowrap">{name}</td>
                        <td className="py-2 pr-2 text-amber-300 font-mono whitespace-nowrap">{freq}</td>
                        <td className="py-2 text-slate-400">{desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── 六、故障速查 ── */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
              <h3 className="text-base font-bold text-rose-400 mb-3">🚑 故障速查（症狀 → 邊度斷 → 點做）</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                {[
                  ["客人冇回覆（首要排查）", "webhook 日誌 accepted 但 chat_logs 冇新記錄＝session 卡死（處理器懸掛）→ POST /api/eshop/admin/session-reset {tenantId, phone} 即復活；或控制台撳「重置」。根治三件套（LLM 60s 超時＋watchdog 自癒）上線後唔應再發生——再發生即回報"],
                  ["客人傳訊息冇回覆", "①webhook 日誌有冇 accepted？冇＝WTS Webhook 轉發斷（去後台設）→②有心跳警示？閘道離線（重綁）→③兩者正常＝查 chat_logs 有冇 assistant"],
                  ["綁定頁出唔到 QR", "登記 flag 賴死——去 WTS Webhook 面板刪除裝置→等幾秒→QR 會出（logout 掣清唔走嘅）"],
                  ["綁定成功但發唔到訊息", "殭屍連線（登記話連線實際死）——同上：面板刪裝置重綁；.in 平道較易發生"],
                  ["AI 答錯／作嘢", "九成係知識庫問題——kb-debug 查檢索（tenantId+q）；KB 冇嘅嘢 AI 應該轉人而唔係作"],
                  ["AI 回覆舊資料（過時價錢等）", "舊 session 歷史記憶——新客人冇事；改完 KB 後同一 session 可能要幾輪先沖淡"],
                  ["額度用盡客人冇人理", "額度完會自動通知老闆＋轉真人（設計咗嘅）；檢查 contact_handoff 手機號有冇填"],
                ].map(([sym, fix]) => (
                  <div key={sym as string} className="py-1.5 border-b border-slate-800/60">
                    <div className="font-semibold text-rose-300 mb-0.5">❓ {sym}</div>
                    <div className="text-slate-400 leading-relaxed">→ {fix}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── 七、應急操作 ── */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
              <h3 className="text-base font-bold text-amber-400 mb-3">🧰 應急操作（管理員 API，x-admin-key）</h3>
              <table className="w-full text-xs">
                <tbody className="divide-y divide-slate-800">
                  {[
                    ["POST /admin/heartbeat-run", "即刻跑一輪閘道心跳（狀態即時刷新）"],
                    ["POST /admin/daily-digest-run?force=1", "即刻補發每日摘要"],
                    ["POST /admin/kb-smoke-run", "即刻跑 RAG 煙霧測試"],
                    ["POST /admin/onboard-followup-run", "即刻跑一輪開通 24h/48h 跟進（欄位冪等，重跑安全）"],
                    ["GET /admin/kb-debug?tenantId=&q=", "查某租戶 RAG 檢索（排 AI 亂答必用）"],
                    ["GET /admin/webhook-logs?tenantId=&limit=", "查入站日誌（排「冇回覆」必用）"],
                    ["POST /internal/init-quota {tenantId,quota}", "重置試用額度"],
                  ].map(([ep, desc]) => (
                    <tr key={ep as string}>
                      <td className="py-2 pr-3 font-mono text-sky-300 whitespace-nowrap align-top">{ep}</td>
                      <td className="py-2 text-slate-400">{desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-[11px] text-slate-500">⚠️ 已知平台差異：.in 舊平台嘅 AI 選單（send-list）唔通（參數格式未知，文字/圖片正常）；新商戶建議統一開 .com 帳號。</p>
            </div>

            {/* ── 八、已上線現況（2026-09-12）── */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
              <h3 className="text-base font-bold text-emerald-400 mb-3">✅ 已上線現況（唔使重做）</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                {[
                  ["付款開通全鏈（線 1）", "/pay 四語頁→KPay UAT 收銀台（季/半年/年繳）→簽名 webhook 自動建租戶→🧾 開通待辦（SLA 2h）→四語歡迎包→門戶三步→active；24h 卡點定向貼士＋48h 停滯升級＋✖️ 取消單（PROD 商戶號待 Captain 拍板）"],
                  ["全站明亮 Mode", "八頁（landing 四頁／門戶／精靈兩頁）＋本後台——左下角 ☀️/🌙 切換，預設暗黑，偏好跨站記住（localStorage aibot_theme）"],
                  ["管理員頻道回音", "登記手機對 bot 有回音（📡＋/help＋未知指令提示；ack 節流 2 分鐘）；AI 照零觸發"],
                  ["定價終版", "Lite HK$199/月（頭兩個月 Premium）｜Pro HK$499/月（永久 Premium）"],
                  ["引擎矩陣", "Premium＝DeepSeek（識圖 -vision-exp）／Basic＝GLM 免費層；方案層級覆蓋租戶 LLM 設定"],
                  ["反濫用上限", "session 上限 Lite 10 / Pro 15（閒置 30 分鐘重置）＋跨號碼 burst Lite 15min/20 / Pro 15min/50；後台 /admin/anti-abuse-config 可配"],
                  ["假期感知", "自動偵測香港公眾假期，Bot 回覆會標註假期狀態"],
                  ["Peak 計量", "isDeepseekPeakNow() 判定高峰段；deepseek_eco_peak_cf 可啟用節流"],
                  ["檔案通道", "WhatsApp PDF 經 connector unpdf 抽字；wizard / portal 用 pdf.js 解析"],
                  ["主域收斂", "wtsaibot.com＝唯一對外主域（gateway 三域並行：wtsaibot＋www＋aibot.jkdcoding.com）；舊域照用唔剷"],
                  ["增值線索", "對話中偵測 Pro 傾向，後台 Leads 頁閃光提示人手跟進"],
                  ["營運透視", "後台 📈 /admin/ops——每租戶 30 日對話/趨勢/轉真人率＋LLM 成本（peak 計價）＋毛利 vs 月費"],
                  ["推薦計劃", "後台 🎁 經銷商模式（D1 v23 四表）：/?r=CODE 歸因→lead→成交回饋自動計"],
                  ["報價閉環", "quote_pdf（E-Corp 首例）：三重門 KB→PDF（R2 字型）→/q/ 短鏈→第2/4日追單；v24/v25 多客化"],
                  ["品牌署名兩層", "租戶 Off／On＋平台預設／On＋自訂（v26）——系統頁 🏷️ 品牌 TAB 管理"],
                  ["portal 教Bot", "商戶門戶客人視角 Q&A 驗收（/portal/ask，每日額度 lite 5／其他 8）"],
                  ["安全加固", "SEC-2026-09：key 遮罩＋24-hex webhook token＋DO 限流＋CORS 閘＋gitleaks CI"],
                ].map(([name, desc]) => (
                  <div key={name as string} className="py-1.5 border-b border-slate-800/60">
                    <div className="font-semibold text-emerald-300 mb-0.5">{name}</div>
                    <div className="text-slate-400 leading-relaxed">{desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── 九、部署四鐵律 ── */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5">
              <h3 className="text-base font-bold text-amber-400 mb-3">🚀 部署四鐵律（2026-09 實錄教訓，一條都唔可以省）</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs">
                {[
                  ["① Pages 必帶 --branch main", "漏咗＝上 Preview 環境，production 見唔到改動（9/10 兩次實錄）"],
                  ["② commit 先，部署後", "worktree 部署只帶 committed code——未 commit＝「0 files uploaded」部署舊版（9/10 favicon 實錄）"],
                  ["③ 乾淨部署前 git grep flag", "origin/main 有執法＋定義喺未合併 WIP＝全平台 AI 靜音 20 分鐘（9/10 auto_reply 實錄）"],
                  ["④ 部署後必 probe＋真對話", "curl status 端點（加 cache-buster）＋實發一則測試訊息先算數；build 管道禁用 | tail（食 exit code）"],
                ].map(([name, desc]) => (
                  <div key={name as string} className="py-1.5 border-b border-slate-800/60">
                    <div className="font-semibold text-amber-300 mb-0.5">{name}</div>
                    <div className="text-slate-400 leading-relaxed">{desc}</div>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[11px] text-slate-500">詳細指令（三 Worker／四 Pages／D1 migration）見 repo docs/DEV-ONBOARDING.md。</p>
            </div>
          </div>
          )}

          {tab === "overview" && (
          <div>

          <div className="mb-6 p-4 md:p-5 bg-slate-900 border border-emerald-700/30 rounded-xl">
            <h3 className="text-base font-semibold text-emerald-300 mb-2">🧬 Employee Pack 執行主鏈</h3>
            <p className="text-xs text-slate-400 mb-4 leading-relaxed">
              角色能力不再直接散落於單一 prompt：Employee Pack 定義角色與版本，facade 選取對外 Skill Pack，tool executor 按 policy 執行已授權工具。
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
              {[
                ["① Employee Pack", "role・version・visibility・executionMode"],
                ["② Facade／Skill Pack", "KB namespace・workflow・policy・skills"],
                ["③ Tool executor", "檢查 facade 與 policy，只執行已授權工具"],
                ["④ 工具與回覆", "Calendar／整合工具；結果回到受控流程"],
              ].map(([title, description]) => (
                <div key={title} className="bg-slate-950/60 border border-slate-800 rounded-lg p-3">
                  <div className="text-emerald-300 font-semibold">{title}</div>
                  <div className="text-slate-500 mt-1 leading-relaxed">{description}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <p className="rounded-lg border border-sky-700/30 bg-sky-950/20 p-3 text-slate-400"><span className="text-sky-300 font-semibold">Migration：</span>按租戶切換 tenant mode，記錄 Pack 版本並驗證對話及工具路徑。</p>
              <p className="rounded-lg border border-amber-700/30 bg-amber-950/20 p-3 text-slate-400"><span className="text-amber-300 font-semibold">Rollback：</span>驗證失敗即回退 legacy；legacy 是回退路徑，不是預設角色，也不刪除 Pack 或知識庫。</p>
              <p className="rounded-lg border border-violet-700/30 bg-violet-950/20 p-3 text-slate-400"><span className="text-violet-300 font-semibold">Integration：</span>只負責外部整合與資料交換，不執行 AI，不進入 LLM 推論鏈。</p>
            </div>
          </div>

          {/* ── 第 1 層：外部角色 ── */}
          <div className="flex flex-col md:flex-row justify-center gap-8 mb-2">
            {/* WhatsApp User */}
            <div className="flex flex-col items-center">
              <div className="px-5 py-3 bg-emerald-900/30 border-2 border-emerald-600 rounded-xl text-center">
                <div className="text-2xl mb-1">💬</div>
                <div className="text-sm font-semibold text-emerald-300">
                  WhatsApp 用戶
                </div>
                <div className="text-xs text-emerald-400/70 mt-0.5">
                  發訊息 / 收訊息
                </div>
              </div>
            </div>

            {/* Admin */}
            <div className="flex flex-col items-center">
              <div className="px-5 py-3 bg-sky-900/30 border-2 border-sky-600 rounded-xl text-center">
                <div className="text-2xl mb-1">🛠️</div>
                <div className="text-sm font-semibold text-sky-300">
                  管理員
                </div>
                <div className="text-xs text-sky-400/70 mt-0.5">
                  Dashboard 後台
                </div>
              </div>
            </div>
          </div>

          {/* 向下箭頭 */}
          <div className="flex justify-center mb-2">
            <div className="flex gap-32 md:gap-48">
              <div className="flex flex-col items-center text-emerald-500/50">
                <span className="text-xs mb-0.5">發訊息</span>
                <span className="text-lg">↓</span>
              </div>
              <div className="flex flex-col items-center text-sky-500/50">
                <span className="text-xs mb-0.5">管理操作</span>
                <span className="text-lg">↓</span>
              </div>
            </div>
          </div>

          {/* ── 第 2 層：接入層 ── */}
          <div className="flex flex-col md:flex-row justify-center gap-4 mb-2">
            {/* WhatsApp */}
            <div className="flex flex-col items-center">
              <div className="px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-center min-w-[160px]">
                <div className="text-sm font-medium text-slate-200">
                  📡 WhatsApp
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  WhatsApp API 閘道
                </div>
              </div>
            </div>

            {/* Dashboard */}
            <div className="flex flex-col items-center">
              <div className="px-4 py-2.5 bg-sky-900/20 border border-sky-700/40 rounded-lg text-center min-w-[160px]">
                <div className="text-sm font-medium text-sky-300">
                  🖥️ aibot-dashboard
                </div>
                <div className="text-xs text-sky-400/60 mt-0.5">
                  React + Cloudflare Pages
                </div>
              </div>
            </div>
          </div>

          {/* 向下箭頭 */}
          <div className="flex justify-center mb-2">
            <div className="flex gap-24 md:gap-40">
              <div className="flex flex-col items-center text-slate-500">
                <span className="text-xs mb-0.5">Webhook</span>
                <span className="text-lg">↓</span>
              </div>
              <div className="flex flex-col items-center text-sky-500/50">
                <span className="text-xs mb-0.5">API 呼叫</span>
                <span className="text-lg">↓</span>
              </div>
            </div>
          </div>

          {/* ── 第 3 層：Workers 核心層 ── */}
          <div className="flex flex-col md:flex-row justify-center gap-4 mb-4">
            {/* WhatsApp Connector */}
            <div className="flex flex-col items-center">
              <div className="px-4 py-3 bg-slate-800 border-2 border-slate-500 rounded-xl text-center min-w-[180px]">
                <div className="text-sm font-bold text-slate-200">
                  🔗 aibot-wa
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  WhatsApp Connector
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  接收 Webhook / 發送回覆
                </div>
                <div className="text-[10px] text-rose-400/70 mt-1">
                  🛡️ 跨號碼 burst limit（Lite 15min/20 · Pro 15min/50）
                </div>
              </div>
              {/* 向下箭頭 */}
              <div className="text-slate-500 my-1">
                <span className="text-xs">Service Binding</span>
                <span className="text-lg ml-1">↓</span>
              </div>
            </div>

            {/* 中間 spacer */}
            <div className="hidden md:block w-8" />

            {/* 管理後台的兩條分支 */}
            <div className="flex flex-col items-center gap-2">
              <div className="flex gap-2">
                {/* 知識庫管理 */}
                <div className="flex flex-col items-center">
                  <div className="px-3 py-2 bg-amber-900/20 border border-amber-700/40 rounded-lg text-center">
                    <div className="text-xs font-semibold text-amber-300">
                      📝 知識庫管理
                    </div>
                    <div className="text-[10px] text-amber-400/60">
                      /admin/ingest
                    </div>
                  </div>
                  <span className="text-amber-500/50 text-lg my-0.5">↓</span>
                </div>
                {/* 租戶管理 */}
                <div className="flex flex-col items-center">
                  <div className="px-3 py-2 bg-violet-900/20 border border-violet-700/40 rounded-lg text-center">
                    <div className="text-xs font-semibold text-violet-300">
                      🏢 租戶列表
                    </div>
                    <div className="text-[10px] text-violet-400/60">
                      /admin/tenants
                    </div>
                  </div>
                  <span className="text-violet-500/50 text-lg my-0.5">↓</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── 第 4 層：AI Core Worker（中央大區塊）── */}
          <div className="flex justify-center mb-4">
            <div className="w-full max-w-2xl bg-emerald-950/20 border-2 border-emerald-700/50 rounded-2xl p-5">
              <div className="text-center mb-4">
                <div className="text-lg font-bold text-emerald-300">
                  🧠 aibot-core（AI Core Worker）
                </div>
                <div className="text-xs text-emerald-500/70 mt-0.5">
                  多租戶 AI 對話核心
                </div>
                <div className="text-[10px] text-orange-400/70 mt-1">
                  🎙️ Voice Notes 轉寫與場景後處理
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* RAG */}
                <div className="bg-slate-950/50 border border-amber-600/40 rounded-lg p-3 text-center">
                  <div className="text-lg mb-1">📚</div>
                  <div className="text-xs font-semibold text-amber-300">
                    RAG 檢索
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1 leading-tight">
                    查 Vectorize
                    <br />
                    知識庫上下文
                  </div>
                </div>

                {/* 租戶配置 */}
                <div className="bg-slate-950/50 border border-violet-600/40 rounded-lg p-3 text-center">
                  <div className="text-lg mb-1">⚙️</div>
                  <div className="text-xs font-semibold text-violet-300">
                    租戶配置
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1 leading-tight">
                    查 D1 資料庫
                    <br />
                    prompt / model / key
                  </div>
                </div>

                {/* LLM */}
                <div className="bg-slate-950/50 border border-emerald-600/40 rounded-lg p-3 text-center">
                  <div className="text-lg mb-1">🤖</div>
                  <div className="text-xs font-semibold text-emerald-300">
                    LLM 推理
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1 leading-tight">
                    Premium＝DeepSeek v4
                    <br />
                    Basic＝GLM 免費層
                  </div>
                </div>

                {/* 額度 */}
                <div className="bg-slate-950/50 border border-rose-600/40 rounded-lg p-3 text-center">
                  <div className="text-lg mb-1">💰</div>
                  <div className="text-xs font-semibold text-rose-300">
                    額度計數
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1 leading-tight">
                    Durable Objects
                    <br />
                    按租戶計數
                  </div>
                </div>

                {/* Core KB（2026-10-10） */}
                <div className="bg-slate-950/50 border border-violet-600/40 rounded-lg p-3 text-center">
                  <div className="text-lg mb-1">🌀</div>
                  <div className="text-xs font-semibold text-violet-300">
                    Core KB 答法層
                  </div>
                  <div className="text-[10px] text-slate-400 mt-1 leading-tight">
                    全平台蒸餾（≥3 租戶）
                    <br />
                    低信心時借力・獨立索引
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── 第 5 層：外部服務 & 數據流回覆 ── */}
          <div className="flex flex-col md:flex-row justify-center gap-6">
            {/* 回覆到 WhatsApp */}
            <div className="flex flex-col items-center">
              <div className="text-slate-500 mb-1">
                <span className="text-lg">↓</span>
                <span className="text-xs ml-1">發送回覆</span>
              </div>
              <div className="px-4 py-2.5 bg-slate-800 border border-slate-600 rounded-lg text-center min-w-[160px]">
                <div className="text-sm font-medium text-slate-200">
                  📡 WhatsApp
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  WhatsApp → 用戶
                </div>
              </div>
              <div className="text-emerald-500/50 mt-1">
                <span className="text-lg">↓</span>
                <span className="text-xs ml-1">用戶收到回覆</span>
              </div>
            </div>

            {/* 右側：數據層 */}
            <div className="flex flex-col items-center gap-3">
              <div className="text-xs text-slate-500 mb-1">數據層</div>

              {/* Vectorize */}
              <div className="px-4 py-2 bg-amber-950/30 border border-amber-700/30 rounded-lg text-center min-w-[180px]">
                <div className="text-xs font-semibold text-amber-300">
                  🗂️ Cloudflare Vectorize
                </div>
                <div className="text-[10px] text-amber-400/50 mt-0.5">
                  bge-m3 向量庫（按租戶隔離）
                </div>
              </div>

              {/* D1 */}
              <div className="px-4 py-2 bg-violet-950/30 border border-violet-700/30 rounded-lg text-center min-w-[180px]">
                <div className="text-xs font-semibold text-violet-300">
                  🗄️ Cloudflare D1
                </div>
                <div className="text-[10px] text-violet-400/50 mt-0.5">
                  租戶・對話・學習・線索・門戶（SQLite）
                </div>
              </div>

              {/* Durable Objects */}
              <div className="px-4 py-2 bg-rose-950/30 border border-rose-700/30 rounded-lg text-center min-w-[180px]">
                <div className="text-xs font-semibold text-rose-300">
                  🔒 Durable Objects
                </div>
                <div className="text-[10px] text-rose-400/50 mt-0.5">
                  按租戶額度計數器
                </div>
              </div>

              {/* R2 Audio */}
              <div className="px-4 py-2 bg-orange-950/30 border border-orange-700/30 rounded-lg text-center min-w-[180px]">
                <div className="text-xs font-semibold text-orange-300">
                  🎙️ R2 aibot-audio
                </div>
                <div className="text-[10px] text-orange-400/50 mt-0.5">
                  語音會議記錄 90 日 lifecycle
                </div>
              </div>
            </div>
          </div>

          {/* ── 近期更新速覽 ── */}
          <div className="mt-4 mb-4 p-4 bg-slate-900/50 border border-slate-700/50 rounded-xl">
            <h3 className="text-sm font-semibold text-emerald-400 mb-3">
              🆕 2026-09-22 已上線更新
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div className="bg-slate-950/40 border border-orange-700/30 rounded-lg p-3">
                <div className="font-semibold text-orange-300 mb-1">🎙️ Voice Notes Phase 1</div>
                <div className="text-slate-400">WhatsApp 語音直鏈 → 異步轉寫 → 場景後處理 → biji 回存；Dashboard 上傳/列表/詳情</div>
              </div>
              <div className="bg-slate-950/40 border border-emerald-700/30 rounded-lg p-3">
                <div className="font-semibold text-emerald-300 mb-1">💳 billing 子域</div>
                <div className="text-slate-400">billing.wtsaibot.com：Lite/Pro 對照、FPS/PayMe/銀行手動付款、WhatsApp 截圖確認</div>
              </div>
              <div className="bg-slate-950/40 border border-rose-700/30 rounded-lg p-3">
                <div className="font-semibold text-rose-300 mb-1">🛡️ 跨號碼 burst limit</div>
                <div className="text-slate-400">Lite 15 分鐘 20 次、Pro 15 分鐘 50 次；達上限 soft-stop＋轉真人，封堵換號繞過</div>
              </div>
            </div>
          </div>

          {/* ── 優化邏輯剖析 ── */}
          <div className="mt-6 pt-4 border-t border-slate-800">
            <h3 className="text-sm font-semibold text-emerald-400 mb-4">
              🧬 優化邏輯剖析
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* RAG 檢索優化 */}
              <div className="bg-slate-950/40 border border-amber-700/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📚</span>
                  <span className="text-sm font-semibold text-amber-300">
                    RAG 檢索優化
                  </span>
                </div>
                <ul className="text-xs text-slate-400 space-y-1.5 list-disc list-inside">
                  <li>
                    <span className="text-slate-300">bge-m3 向量模型</span>（1024 維）生成查詢向量
                  </li>
                  <li>
                    <span className="text-slate-300">topK = 5</span> 檢索最相關知識片段
                  </li>
                  <li>
                    <span className="text-slate-300">tenant_id filter</span> 確保租戶間知識庫隔離
                  </li>
                  <li>
                    <span className="text-slate-300">字元預算截斷</span>：RAG 上下文最多 8,000 字元，超出即停
                  </li>
                </ul>
              </div>

              {/* Token 預算管理 */}
              <div className="bg-slate-950/40 border border-sky-700/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">📏</span>
                  <span className="text-sm font-semibold text-sky-300">
                    Token 預算管理
                  </span>
                </div>
                <ul className="text-xs text-slate-400 space-y-1.5 list-disc list-inside">
                  <li>
                    用戶查詢 <span className="text-slate-300">≤ 2,000 字元</span>
                  </li>
                  <li>
                    RAG 上下文 <span className="text-slate-300">≤ 8,000 字元</span>
                  </li>
                  <li>
                    對話歷史 <span className="text-slate-300">≤ 4,000 字元</span>，最多 <span className="text-slate-300">24 列（約 12 輪）</span>
                  </li>
                  <li>
                    總上下文嚴格控制，防止 LLM 上下文溢出與費用爆增
                  </li>
                </ul>
              </div>

              {/* 多 LLM 路由 */}
              <div className="bg-slate-950/40 border border-emerald-700/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🔀</span>
                  <span className="text-sm font-semibold text-emerald-300">
                    多 LLM 路由
                  </span>
                </div>
                <ul className="text-xs text-slate-400 space-y-1.5 list-disc list-inside">
                  <li>
                    <span className="text-slate-300">Premium 主力＝DeepSeek</span>（v4-flash；識圖必用 -vision-exp）；空回覆自動重試一次（冇 GLM 後備）
                  </li>
                  <li>
                    <span className="text-slate-300">Basic 主力＝GLM 免費層</span>（glm-4-flash／4v-flash）→ 故障後備 DeepSeek →（routing 關閉／peak eco）CF Workers AI
                  </li>
                  <li>
                    Peak/Off-Peak 雙軌計量＋Eco 節流；全部 LLM 外呼 <span className="text-slate-300">60 秒超時</span>（卡死根治）
                  </li>
                  <li>
                    方案層級（plan_tier）決定引擎層，覆蓋租戶原本 llm_provider / llm_model 設定
                  </li>
                </ul>
              </div>

              {/* 系統提示詞工程 */}
              <div className="bg-slate-950/40 border border-violet-700/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">🛡️</span>
                  <span className="text-sm font-semibold text-violet-300">
                    系統提示詞工程
                  </span>
                </div>
                <ul className="text-xs text-slate-400 space-y-1.5 list-disc list-inside">
                  <li>
                    <span className="text-slate-300">最高指令框架</span>：強制 Bot 遵循商家設定，防止角色扮演攻擊
                  </li>
                  <li>
                    多段組裝（safeSystemPrompt 單點）：<span className="text-slate-300">鐵律＋&lt;merchant_instructions&gt;＋軟技能＋格式守則＋聆聽層＋（富媒體／報價附錄）＋&lt;knowledge_base&gt;</span>
                  </li>
                  <li>
                    提供 6 種行業預設模板，一鍵套用（客服、銷售、技術、餐飲、教育、醫療）
                  </li>
                  <li>
                    每租戶獨立 system_prompt，D1 持久化存儲
                  </li>
                </ul>
              </div>

              {/* 原子額度控制 */}
              <div className="bg-slate-950/40 border border-rose-700/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">💰</span>
                  <span className="text-sm font-semibold text-rose-300">
                    原子額度控制
                  </span>
                </div>
                <ul className="text-xs text-slate-400 space-y-1.5 list-disc list-inside">
                  <li>
                    <span className="text-slate-300">Durable Objects</span> 保證高併發下原子扣減
                  </li>
                  <li>
                    每租戶獨立 DO 實例，<span className="text-slate-300">按 tenant_id 隔離</span>
                  </li>
                  <li>
                    trial 用戶超額時自動返回轉接連結，引導升級
                  </li>
                  <li>
                    paid 用戶無額度限制，DO 僅作統計用途
                  </li>
                </ul>
              </div>

              {/* 知識庫自動切片 */}
              <div className="bg-slate-950/40 border border-teal-700/30 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">✂️</span>
                  <span className="text-sm font-semibold text-teal-300">
                    知識庫自動切片
                  </span>
                </div>
                <ul className="text-xs text-slate-400 space-y-1.5 list-disc list-inside">
                  <li>
                    按<span className="text-slate-300">空行分隔段落</span>智能分塊
                  </li>
                  <li>
                    每 chunk <span className="text-slate-300">≤ 500 字元</span>，語義完整不割裂
                  </li>
                  <li>
                    上傳新內容時<span className="text-slate-300">自動刪除舊向量</span>，避免髒數據累積
                  </li>
                  <li>
                    Vectorize metadata 標記 tenant_id，實現物理隔離
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* ── 說明文字 ── */}
          <div className="mt-6 pt-4 border-t border-slate-800 space-y-2 text-xs text-slate-400">
            <p>
              <span className="text-emerald-400 font-semibold">對話流：</span>
              WhatsApp 用戶發訊息 → WTS Webhook 平台回調 → gateway（wtsaibot.com 路由）→ aibot-wa → aibot-core 查租戶配置 + RAG 檢索知識庫 → LLM 推理 → WhatsApp 發送回覆
            </p>
            <p>
              <span className="text-sky-400 font-semibold">管理流：</span>
              管理員在 Dashboard 操作 → 知識庫上傳存入 Vectorize / 租戶設定存入 D1 → aibot-core 即時讀取使用
            </p>
            <p>
              <span className="text-amber-400 font-semibold">多租戶隔離：</span>
              每個租戶有獨立的知識庫向量空間（Vectorize metadata 標記 tenant_id）、獨立的 D1 資料列、獨立的額度計數器
            </p>
          </div>
          </div>
          )}

          {/* ═══════════ TAB：Prompt 組裝 ═══════════ */}
          {tab === "prompt" && (
          <div className="space-y-0">
            <div className="mb-4 p-4 bg-violet-950/20 border border-violet-700/30 rounded-xl text-sm text-violet-200/90 max-w-3xl mx-auto">
              🧩 每則 AI 回覆都不是「一個 prompt」，而是<strong>四層材料即場組裝</strong>的結果——
              平台鐵律管「點樣講嘢」、租戶人格管「係邊個」、軟技能管「點樣對待客人」、知識庫管「講咩事實」。
              改哪一層，就在哪一層生效（全平台 vs 單租戶 vs 語義相關）。
            </div>
            <FlowStep icon="🛡️" title="① [系統最高指令]——平台鐵律（全租戶統一，代碼寫死）" sub="禁連結・禁內部術語（知識庫/prompt/模型）・防注入・誠實轉真人。與「租戶設定」無關，任何店都一樣——安全同說話衛生係平台責任" tone="rose" />
            <FlowArrow label="＋" />
            <FlowStep icon="👤" title="② &lt;merchant_instructions&gt;——租戶人格（每店獨立，D1 system_prompt）" sub="小美定阿Bot・語氣・稱呼・禁忌。師傅開通時幫老闆生成，後台可改——「係邊個」嘅問題喺呢層答" tone="violet" />
            <FlowArrow label="＋" />
            <FlowStep icon="🤝" title="③ &lt;platform_skills&gt;——軟技能（全租戶統一，十條行為規則）" sub="先俾着數・先同理後探索再解答（異議處理）・細承諾階梯・誠實緊迫・搞清對話對象（轉介場景）・讀心有度・對準客人最新嗰句等——「點樣對待客人」嘅銷售心理學，Cialdini×Berger 操作化" tone="emerald" />
            <FlowArrow label="＋" />
            <FlowStep icon="🎛️" title="③b 平台功能層（按租戶功能／訊息內容注入）" sub="WhatsApp 格式守則（單星號粗體・一點一行）｜聆聽三問＋〖MEM〗客人記憶回寫｜富媒體標記協議（rich_media 開啟時）｜報價附錄（quote_pdf 開啟時）｜識圖指引（本輪收圖時）" tone="sky" />
            <FlowArrow label="＋" />
            <FlowStep icon="📚" title="④ &lt;knowledge_base&gt;——知識事實（每店獨立，RAG top-5 按話題抽）" sub="營業時間・價目・政策——語義相關先會出現，所以只放事實，唔放行為規則（概率性檢索靠唔住）" tone="amber" />
            <FlowArrow label="＋ 對話歷史（約 12 輪）＋ 場境狀態（歡迎回來／情緒）＋ 日期／香港假期・客人語體同頻・附件文字（fileText）" />
            <FlowStep icon="🧠" title="組裝成最終 System Prompt → 送 LLM（Premium＝DeepSeek・Basic＝GLM 免費層・後備 Workers AI）" sub="一次對話＝四層叠加＋動態注入；AI 嘅「理解」由四層共同塑造" tone="sky" />
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl mx-auto text-xs">
              <div className="bg-slate-950/40 border border-slate-700/40 rounded-lg p-3">
                <span className="text-emerald-300 font-semibold">改哪層？——判斷口訣：</span>
                <span className="text-slate-400">事實放 KB・人格放租戶 prompt・鐵律同軟技能放平台層（母機規則 65：KB=是什麼／Prompt=何時做／代碼與鐵律=怎麼做）</span>
              </div>
              <div className="bg-slate-950/40 border border-slate-700/40 rounded-lg p-3">
                <span className="text-sky-300 font-semibold">意向理解點嚟：</span>
                <span className="text-slate-400">③嘅「先探索後回應」教 AI 問動機・歷史層記前文脈絡・智能分流讀情緒——令「估意向」變成「問意向＋驗證」，唔靠猜</span>
              </div>
              <div className="bg-slate-950/40 border border-slate-700/40 rounded-lg p-3">
                <span className="text-amber-300 font-semibold">動態注入（9 月新增）：</span>
                <span className="text-slate-400">香港公眾假期感知・客人語體同頻（書面/口語跟客人）・聆聽層三招（三問清單＋情緒深讀＋customer_memory）・附件文字隨 prompt 入場</span>
              </div>
            </div>
          </div>
          )}

          {/* ═══════════ TAB 2：對話與轉真人 ═══════════ */}
          {tab === "chat" && (
          <div className="space-y-0">
            <FlowStep icon="💬" title="客人發 WhatsApp 訊息" sub="任何時間・任何語言" tone="emerald" />
            <FlowArrow label="WTS Webhook（隨機 token 驗證・webhook_logs 全程留痕）" />
            <FlowStep icon="🔗" title="aibot-wa（Connector Worker）" sub="管理員頻道（/pause・/resume・/reply・/status・/help＋非指令回音，節流 2 分鐘）｜登記手機（handoff＋ops）統一行 admin_command／ops_admin_command 審計——唔入 AI、唔入 chat_logs｜/c 短連結＋控制台｜租戶級 CRM 轉發閘道（hkhta 直達 CRM 唔經 AI）→ 其餘交落去｜🎛️ 互動發送層 rich-media.ts（AI 標記→原生按鈕/選單/圖片；Set A/B＋平台地雷一處管；referral/eshop 共享）" tone="slate" />
            <FlowArrow label="Service Binding" />
            <FlowStep icon="🫀" title="SessionCoordinator DO（每租戶＋每客人一個）" sub="訊息合併視窗（分段輸入先合併）｜可中斷生成（新訊息到即棄舊輪）｜打字節奏分段發送｜12 輪記憶｜閒置 5 分鐘「歡迎返嚟」｜⏱️ LLM／發送 60s 超時＋90 秒 watchdog 自癒（卡死根治三件套）" tone="sky" />
            <FlowArrow label="RPC ask()" />
            <FlowStep icon="🧠" title="aibot-core 對話引擎" sub="額度檢查（DO 原子扣減）→ RAG 檢索 top-5 → 安全框架＋人格 prompt＋歷史組裝 → LLM 推理" tone="emerald" />

            {/* 智能分流分支 */}
            <div className="my-3 mx-auto max-w-2xl bg-rose-950/30 border border-rose-700/40 rounded-xl p-4">
              <div className="text-sm font-semibold text-rose-300 mb-2">⚖️ 智能分流：識就答，唔識就轉（唔會屈生客）</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <div className="bg-slate-950/50 rounded-lg p-2.5 border border-rose-800/40">
                  <div className="text-rose-300 font-medium">客人講「轉真人」</div>
                  <div className="text-slate-500 mt-0.5">關鍵字偵測</div>
                </div>
                <div className="bg-slate-950/50 rounded-lg p-2.5 border border-rose-800/40">
                  <div className="text-rose-300 font-medium">連續 3 輪低信心</div>
                  <div className="text-slate-500 mt-0.5">RAG 檢索分數 &lt; 0.5（FTS 命中唔算；閒置 24 小時重置計數）</div>
                </div>
                <div className="bg-slate-950/50 rounded-lg p-2.5 border border-rose-800/40">
                  <div className="text-rose-300 font-medium">負面情緒</div>
                  <div className="text-slate-500 mt-0.5">低信心＋情緒疊加即刻觸發</div>
                </div>
              </div>
            </div>

            <FlowArrow label="命中任一條件" tone="rose" />
            <FlowStep icon="🤝" title="引導式邀請（9/6 上線）：先問過客人才轉" sub="AI 出 WhatsApp 原生按鈕「✅ 好，轉真人／唔使，我繼續問」——客人應承先轉；擰拒絕＝重置低信心計數照用 AI，唔會硬搶｜邀請語偵測中英馬三語（HANDOFF_INVITE_RE）｜反濫用：session 上限 Lite 10／Pro 15（閒置 30 分鐘重置）＋跨號碼 burst Lite 15min/20／Pro 15min/50，逾上限 soft-stop 轉真人" tone="rose" />
            <FlowArrow label="客人應承／明講轉真人" tone="rose" />
            <FlowStep icon="📢" title="即刻 WhatsApp 通知老闆手機（最多 3 位）" sub="附對話摘要＋/c 短連結（原生 url/call 按鈕）｜客人唔使重複講一次" tone="rose" />
            <FlowArrow label="同時" tone="rose" />
            <FlowStep icon="⏸️" title="AI 自動暫停該客人 30 分鐘" sub="期間訊息只記錄唔送 AI｜老闆可提早 /resume 復工｜30 分鐘到期自動恢復＋觸發學習迴圈" tone="amber" />
            <FlowArrow label="老闆兩種回法" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
              <FlowStep icon="📱" title="手機指令" sub="/reply 代覆｜/pause 續停｜/resume 復工" tone="slate" compact />
              <FlowStep icon="🖥️" title="轉真人控制台（/c/短碼）" sub="泡泡對話 UI・PIN 手機尾 4 位・直接回覆客人・「重置」掣救卡死對話" tone="slate" compact />
            </div>
            <div className="mt-4 text-center text-xs text-emerald-400/70">
              ↻ 接管結束自動觸發「自我學習迴圈」（見下一個分頁）
            </div>
          </div>
          )}

          {/* ═══════════ TAB 3：自我學習迴圈 ═══════════ */}
          {tab === "learning" && (
          <div className="space-y-0">
            <div className="mb-4 p-4 bg-amber-950/20 border border-amber-700/30 rounded-xl text-sm text-amber-200/90 max-w-3xl mx-auto">
              🧠 <span className="font-semibold">知識庫學習迴圈（KB Learning Loop，v1.3.0）</span>——
              老闆每次人手回覆客人，AI 都會偷師：接管結束後自動分析「真人點樣答」，
              產生知識庫建議，<span className="text-amber-300 font-medium">人工審批後</span>先寫入。Bot 越用越識答你間舖嘅嘢。
            </div>
            <FlowStep icon="✅" title="① 人工接管結束" sub="老闆 /resume・控制台撳「恢復 AI」・或 30 分鐘到期自動恢復" tone="emerald" />
            <FlowArrow label="非阻塞觸發（失敗只記 log，唔阻主流程）" />
            <FlowStep icon="🔍" title="② analyzeHandoffForKB()" sub="讀「轉接摘要」之後嘅對話——必須含真人回覆先分析，最多產生 3 則 Q&A 建議" tone="sky" />
            <FlowArrow label="DeepSeek 分析（60s 超時；無 key 自動回退 Workers AI）" />
            <FlowStep icon="📋" title="③ kb_changelog 表（status=pending）" sub="D1 migration v9｜每則建議含問題＋建議答案＋來源對話" tone="violet" />
            <FlowArrow label="等管理員審批" />
            <div className="mx-auto max-w-2xl bg-sky-950/30 border border-sky-700/40 rounded-xl p-4">
              <div className="text-sm font-semibold text-sky-300 mb-2">🧠 Dashboard「知識庫學習」頁 — 人工把關</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                <div className="bg-slate-950/50 rounded-lg p-2.5 border border-emerald-800/40 text-center">
                  <div className="text-emerald-300 font-medium">✅ 採納</div>
                  <div className="text-slate-500 mt-0.5">原稿寫入</div>
                </div>
                <div className="bg-slate-950/50 rounded-lg p-2.5 border border-amber-800/40 text-center">
                  <div className="text-amber-300 font-medium">✏️ 修改後採納</div>
                  <div className="text-slate-500 mt-0.5">改好先入庫</div>
                </div>
                <div className="bg-slate-950/50 rounded-lg p-2.5 border border-rose-800/40 text-center">
                  <div className="text-rose-300 font-medium">🚫 忽略</div>
                  <div className="text-slate-500 mt-0.5">唔係知識嘅嘢</div>
                </div>
              </div>
            </div>
            <FlowArrow label="採納時" />
            <FlowStep icon="➕" title="④ /admin/ingest-append 增量寫入 Vectorize" sub="只加唔刪（唔會清走既有知識庫）｜區別於覆蓋式 /admin/ingest" tone="amber" />
            <FlowArrow label="下一次客人問同類問題" />
            <FlowStep icon="🎯" title="⑤ Bot 用新知識作答 — 越用越聰明" sub="RAG top-5 檢索即時命中新向量" tone="emerald" />
            <div className="mt-4 text-center text-xs text-amber-400/70">
              ↻ 再有下一次人工接管 → 返回 ①，循環沉澱
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl mx-auto text-xs">
              <div className="bg-slate-950/40 border border-slate-700/40 rounded-lg p-3">
                <span className="text-amber-300 font-semibold">📝 人工整理（並行渠道）：</span>
                <span className="text-slate-400">Dashboard 知識庫頁全量上傳（覆蓋式）／ingest-append 增量</span>
              </div>
              <div className="bg-slate-950/40 border border-slate-700/40 rounded-lg p-3">
                <span className="text-violet-300 font-semibold">🎛️ 功能模塊：</span>
                <span className="text-slate-400">kb_learning_loop（預設開啟・可逐租戶關閉・可獨立計價）</span>
              </div>
              <div className="bg-slate-950/40 border border-slate-700/40 rounded-lg p-3">
                <span className="text-emerald-300 font-semibold">🚪 知識庫唔止一條入路：</span>
                <span className="text-slate-400">師傅 KB 訪談（材料夾→apply 併入）／門戶自助貼上／PDF 抽取——全部同一個 Vectorize</span>
              </div>
            </div>

            {/* ── 平台級放大器：Core KB 蒸餾飛輪（2026-10-10）── */}
            <div className="mt-8 mb-4 p-4 bg-violet-950/20 border border-violet-700/30 rounded-xl text-sm text-violet-200/90 max-w-3xl mx-auto">
              🌀 <span className="font-semibold">平台級放大器：Core KB 蒸餾飛輪（2026-10-10 上線）</span>——
              上面條迴圈係<span className="text-violet-300 font-medium">每個租戶自己變聰明</span>；
              Core KB 再進一步：集合<span className="text-violet-300 font-medium">全部租戶</span>嘅對話，
              蒸餾出「呢類問題點樣答」嘅通用答法（<span className="text-violet-300 font-medium">租戶零感知</span>），
              全平台新租戶即刻受惠。<span className="text-slate-400">管理＝/system → 🌀 Core KB 蒸餾 TAB</span>
            </div>
            <FlowStep icon="📨" title="A① 全平台對話流（30 日窗）" sub="每個 session 首則客人訊息——唔會改動原記錄" tone="sky" />
            <FlowArrow label="匿名化閘：剝電話/電郵/URL/編號——唔過閘嘅樣本就地丟棄" />
            <FlowStep icon="🧩" title="A② DeepSeek 聚類意圖 → A③ ≥3 租戶共現先合格" sub="單一租戶嘅模式永遠留返該租戶 KB，唔會入 Core（保守起步・Captain 拍板）" tone="violet" />
            <FlowArrow label="每週三 HKT 18:00 off-peak cron（0 10 * * 3）＋/system 手動掣" />
            <FlowStep icon="⚗️" title="A④ 提煉「答法結構」→ A⑤ Captain /system 審批" sub="只儲點樣答（步驟/語氣/避免乜），永不儲答乜（商家事實）；九語全蒸" tone="amber" />
            <FlowArrow label="approve＝bge-m3 嵌入 → VECTOR_CORE_KB 獨立索引（與租戶 KB 物理分層）" />
            <FlowStep icon="🪜" title="B 檢索梯：租戶 KB 冇命中 → Core KB 套答法 → LLM 組句" sub="fail-open：Core KB 任何失敗永不阻塞答問；具體事實只准來自租戶知識庫" tone="emerald" />
            <FlowArrow label="反饋環（零人工）：❌AI 答錯 → wrong_count++；wrong 率 ≥30%＋≥10 hits 自動退役" />
            <div className="mt-2 text-center text-xs text-violet-400/70">
              ↻ 長尾對話落入 30 日窗 → 下輪蒸餾候選——飛輪自己閉口，越用越聰明＋越用越平
            </div>
          </div>
          )}

          {/* ═══════════ TAB 4：開通精靈 ═══════════ */}
          {tab === "onboard" && (
          <div className="space-y-0">
            <div className="mb-8 max-w-3xl mx-auto rounded-xl border border-emerald-700/30 bg-slate-900 p-4 md:p-5">
              <h3 className="text-base font-semibold text-emerald-300 mb-2">🧬 Employee Pack 角色開通基線</h3>
              <p className="text-xs text-slate-400 mb-4 leading-relaxed">
                付款與 WhatsApp 通道開通後，租戶以 Employee Pack 作為角色主線；legacy 只保留作回退，不是新租戶預設角色。
              </p>
              <FlowStep icon="🧬" title="① 選定 Employee Pack 與版本" sub="確認 role・visibility・executionMode；新開通與 migration 均記錄 packId／version，避免無版本的角色切換" tone="emerald" compact />
              <FlowArrow label="解析 defaultFacade，或選定已核准 facade" />
              <FlowStep icon="🎛️" title="② 綁定 Facade／Skill Pack" sub="按 facade 載入 KB namespace・workflow・policy・skills；租戶只取得該 facade 暴露的能力" tone="violet" compact />
              <FlowArrow label="工具動作統一交 tool executor" />
              <FlowStep icon="🛠️" title="③ Tool executor 驗證與執行" sub="先檢查 policy 與授權，再執行 Calendar 等工具；integration 只作資料整合，不執行 AI" tone="sky" compact />
              <FlowArrow label="Migration 驗證＋可逆 rollback" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
                <FlowStep icon="🧪" title="④a Migration 驗證" sub="切換 tenant mode 後驗證角色回覆、KB 隔離及工具結果，保留 Pack 版本供審計" tone="emerald" compact />
                <FlowStep icon="↩️" title="④b Rollback 回退" sub="驗證失敗即切回 legacy 回退路徑；不刪除 Pack、facade 或知識庫，修復後可重新 migration" tone="amber" compact />
              </div>
            </div>

            <div className="mb-4 p-4 bg-emerald-950/20 border border-emerald-700/30 rounded-xl text-sm text-emerald-200/90 max-w-3xl mx-auto">
              💳 <span className="font-semibold">線 1 付款開通漏斗（2026-09-12 全鏈上線）</span>——
              客人自助付款→系統自動開通隊列→ops 兩小時 SLA 完成開通→客人門戶三步開業。全程狀態機：
              <span className="font-mono text-emerald-300"> awaiting_payment → awaiting_provision → provisioned → welcome_sent → active</span>（cancelled＝人手作廢）。
            </div>
            <FlowStep icon="🖥️" title="① wtsaibot.com/pay（四語）" sub="填店名＋WhatsApp 電話→揀方案（Lite/Pro）＋週期（季/半年/年繳）→撳付款掣" tone="sky" />
            <FlowArrow label="POST /api/onboard/checkout（awaiting_payment 前置單）" />
            <FlowStep icon="💳" title="② KPay 全託管收銀台（UAT 環境先行）" sub="自動建單＋自簽收銀台 URL｜FPS/PayMe/信用卡｜30 分鐘唔付款自動過期（單可 ✖️ 取消）" tone="emerald" />
            <FlowArrow label="付款成功 → KPay 簽名通知（平台公鑰驗簽＋±10min 防重放）→ 自動建租戶＋ops 收 🧾 通知" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
              <FlowStep icon="🧾" title="③a Dashboard 開通中心" sub="五段狀態分開檢查；自動開通／重新對帳為主，RPMSMS Key 手動開通降為後備" tone="amber" compact />
              <FlowStep icon="📤" title="③b 四語歡迎包" sub="server-side 由 admin 中央號發（URL 帶 ?lang= 同歡迎包語言）；臨時密碼發送一刻先產生" tone="violet" compact />
            </div>
            <FlowArrow label="客人撳訊息內連結登入門戶（wtsaibot.com/merchant）" />
            <FlowStep icon="🚪" title="④ 門戶開業三步卡" sub="①教 Bot（貼營業時間/價目）→ ②掃碼綁定 WhatsApp（金絲雀驗證）→ ③試玩場講一句 → 🎉 開業（TAB 即解鎖）" tone="sky" />
            <FlowArrow label="自動跟進（每小時 cron）" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
              <FlowStep icon="⏰" title="⑤a 24h 卡點定向貼士" sub="實時查三步進度，只提未完成嗰步（四語×3 版本；notifier 缺＝唔發等下個鐘）" tone="amber" compact />
              <FlowStep icon="🆘" title="⑤b 48h 停滯升級" sub="仍未開業 → system_logs 留案＋ops 手機收「⏰ 開通停滯」人手跟進；發送失敗 flag 自動回滾重試" tone="rose" compact />
            </div>
            <div className="mt-3 text-center text-xs text-slate-500">
              收款金額 server 端真相源（季繳 747/1797 起）｜KPay PROD 切換待 Captain 拍板商戶號＋IP 白名單（母機規則 129）｜人手後備：「➕ 人工收款登記」＋📋 複製歡迎包
            </div>

            <div className="mt-8 mb-4 p-4 bg-sky-950/20 border border-sky-700/30 rounded-xl text-sm text-sky-200/90 max-w-3xl mx-auto">
              🧙 <span className="font-semibold">對話式 Onboarding（阿Bot師傅 v4——KB 訪談員＋商業顧問）</span>——
              每個租戶一條專屬連結，新老闆傾 15 分鐘就完成最難嘅兩項設定（客服人格＋知識庫），
              最後撳一個掣自動裝落租戶。行業模板庫 <span className="text-emerald-300 font-medium">115 塊</span>打底、
              覆述硬格式入材料夾、📎 相片 PDF 直讀、增值線索自動落後台。全程毋須 WhatsApp 線、毋須帳號。
            </div>
            <FlowStep icon="🖥️" title="① Dashboard 租戶列表 → ✏️ 編輯 →「🧙 開通連結」TAB" sub="每租戶一條專屬 URL：?t=租戶ID&amp;n=店名&amp;w=webhook_token（w＝不可猜安裝權限）" tone="sky" />
            <FlowArrow label="複製＋發俾客戶（WhatsApp／SMS／任何渠道）" />
            <FlowStep icon="🌐" title="② aibot-wizard Pages 站" sub="瀏覽器直接對話｜金鑰全程服務端：/api/ask 代持師傅 key・/api/apply 代持 admin key" tone="slate" />
            <FlowArrow label="/api/ask" />
            <div className="mx-auto max-w-2xl bg-emerald-950/20 border border-emerald-700/40 rounded-xl p-4">
              <div className="text-sm font-semibold text-emerald-300 mb-2">🧙 onboard_helper（阿Bot師傅）五部曲・15 分鐘</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs text-center">
                <div className="bg-slate-950/50 rounded-lg p-2 border border-emerald-800/30">開場定調<div className="text-slate-500 mt-0.5">店名＋行業</div></div>
                <div className="bg-slate-950/50 rounded-lg p-2 border border-emerald-800/30">人格設定<div className="text-slate-500 mt-0.5">3 條偏好題</div></div>
                <div className="bg-slate-950/50 rounded-lg p-2 border border-emerald-800/30">知識庫收集<div className="text-slate-500 mt-0.5">12 主題</div></div>
                <div className="bg-slate-950/50 rounded-lg p-2 border border-emerald-800/30">品質自檢<div className="text-slate-500 mt-0.5">唔作嘢</div></div>
                <div className="bg-slate-950/50 rounded-lg p-2 border border-emerald-800/30">嚴格交收<div className="text-slate-500 mt-0.5">P0/P1/P2/END</div></div>
              </div>
              <div className="mt-2 text-[11px] text-slate-500">鐵律：一次只問一條・唔重複唔兜圈｜smart_triage 已關閉（防傾談被自動轉人中斷）｜冇料主題標【待補】</div>
            </div>
            <FlowArrow label="師傅輸出 ━━P0━━Bot名 ━━P1━━Prompt ━━P2━━【主題】塊 ━━END━━" />
            <FlowStep icon="⚡" title="③ 前端自動解析 →「一鍵裝落 &lt;租戶&gt;」面板" sub="預覽 Bot名・Prompt 字數・知識庫塊數｜冇 w 權限嘅連結唔會出現安裝掣" tone="amber" />
            <FlowArrow label="POST /api/apply（服務端核對 tenant_id＋webhook_token，不符→403）" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
              <FlowStep icon="⚙️" title="④a 寫入人格" sub="system_prompt＋bot_name → 該租戶" tone="violet" compact />
              <FlowStep icon="📚" title="④b 灌知識庫" sub="chunks 模式一主題一塊 → Vectorize" tone="amber" compact />
            </div>
            <FlowArrow label="完成" tone="emerald" />
            <FlowStep icon="✅" title="⑤ 租戶即刻可用" sub="sessionId＝onboard-&lt;租戶ID&gt;-&lt;時間戳&gt;（chat-logs 可追溯）｜面板附「去後台檢視」連結｜想改＝叫師傅改完再裝過（覆蓋式）" tone="emerald" />
            <FlowArrow label=" Value Ladder（9/7 全套上線）" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto">
              <FlowStep icon="🧪" title="⑥a 裝完即試——試玩沙盒" sub="用真知識庫考 bot，20 輪演示（用完完結、剩 5 提醒、唔計正式額度）" tone="sky" compact />
              <FlowStep icon="🔥" title="⑥b 商機捕捉——增值線索" sub="傾談中嘅痛點／預算自動記入 lead_signals，Dashboard「增值線索」頁跟進（側欄紅點提示）" tone="rose" compact />
            </div>
            <div className="mt-3 text-center text-xs text-slate-500">
              💰 付款閘（REQUIRE_PAID_FOR_KB）控制未付款租戶可唔可以裝知識庫｜訪談 30 輪軟上限引導出交收（25 輪預告）｜裝完商戶可喺門戶「教Bot」TAB 用客人視角再驗收（每日額度 lite 5／其他 8）
            </div>
          </div>
          )}
    </>
  );
}

/* ── 流程圖小組件 ── */
const TONE: Record<string, string> = {
  slate: "bg-slate-800/60 border-slate-600 text-slate-200",
  emerald: "bg-emerald-900/25 border-emerald-700/50 text-emerald-200",
  sky: "bg-sky-900/25 border-sky-700/50 text-sky-200",
  amber: "bg-amber-900/25 border-amber-700/50 text-amber-200",
  violet: "bg-violet-900/25 border-violet-700/50 text-violet-200",
  rose: "bg-rose-900/25 border-rose-700/50 text-rose-200",
};

function FlowStep({
  icon,
  title,
  sub,
  tone = "slate",
  compact = false,
}: {
  icon: string;
  title: string;
  sub?: string;
  tone?: string;
  compact?: boolean;
}) {
  return (
    <div
      className={`mx-auto w-full max-w-2xl border rounded-xl text-center ${
        compact ? "px-4 py-2.5" : "px-5 py-3.5"
      } ${TONE[tone] ?? TONE.slate}`}
    >
      <div className={`${compact ? "text-sm" : "text-base"} font-semibold`}>
        {icon} {title}
      </div>
      {sub && <div className="text-xs text-slate-400 mt-1 leading-relaxed">{sub}</div>}
    </div>
  );
}

function FlowArrow({ label, tone = "slate" }: { label?: string; tone?: string }) {
  const color =
    tone === "rose" ? "text-rose-500/70" : tone === "emerald" ? "text-emerald-500/70" : "text-slate-500";
  return (
    <div className={`flex flex-col items-center my-1.5 ${color}`}>
      {label && <span className="text-[10px] mb-0.5">{label}</span>}
      <span className="text-lg leading-none">↓</span>
    </div>
  );
}
